/**
 * Réveil natif (FCM sur Android, APNs sur iOS) — Capacitor.
 *
 * Web : no-op immédiat, le navigateur reste sur le Web Push de l'ORIP.
 * L'inscription ne demande la permission qu'une fois (mémoïsée), et renvoie le
 * jeton d'appareil pour qu'il puisse être envoyé au Worker.
 *
 * Chaîne complète (bloquée sur vos comptes — voir docs/CAPACITOR-FIRST.md § FCM) :
 *   1. google-services.json (Firebase) dans android/app/ → le plugin Gradle
 *      existant s'active alors automatiquement ;
 *   2. POST /mobile/push-token côté Worker (table de jetons) ;
 *   3. envoi FCM depuis le Worker (service account) pour le réveil d'appel.
 */
import { Capacitor } from '@capacitor/core';
import { PushNotifications } from '@capacitor/push-notifications';
import { dataApi } from '../api/dataApi';

let pending: Promise<string | null> | null = null;

/**
 * Demande la permission de notification puis renvoie le jeton FCM/APNs,
 * ou null (web, refus de l'utilisateur, timeout du registraire).
 * Mémoïsée : un seul appel par vie de session, qu'il réussisse ou échoue.
 */
export function registerForPush(): Promise<string | null> {
  pending ??= request();
  return pending;
}

/**
 * Envoie le jeton au Worker (`POST /mobile/push-token`) — à appeler une fois
 * connecté : le tenant est déduit de la session, jamais du corps.
 * Répété à chaque changement de compte, pour réattribuer l'appareil.
 */
export async function syncPushToken(token: string): Promise<void> {
  try {
    const platform = Capacitor.getPlatform() === 'ios' ? 'ios' : 'android';
    await dataApi.registerPushToken({ token, platform });
    console.log(`[push] jeton ${platform} transmis au Worker`);
  } catch (error) {
    // Jamais bloquant : sans jeton côté serveur, les notifications restent
    // visibles dans l'application.
    console.warn('[push] transmission du jeton impossible', error);
  }
}

async function request(): Promise<string | null> {
  if (!Capacitor.isNativePlatform()) return null;

  try {
    const { receive } = await PushNotifications.requestPermissions();
    if (receive !== 'granted') return null;

    await PushNotifications.register();

    const token = await new Promise<string | null>((resolve) => {
      const timer = setTimeout(() => resolve(null), 10_000);
      void PushNotifications.addListener('registration', (t) => {
        clearTimeout(timer);
        resolve(t.value || null);
      });
      void PushNotifications.addListener('registrationError', () => {
        clearTimeout(timer);
        resolve(null);
      });
    });

    if (token) {
      // Jamais le jeton complet dans les logs (il identifie l'appareil) :
      // seul son préfixe est journalisé, suffisant pour vérifier un inscription.
      console.log(`[push] jeton appareil ${token.slice(0, 12)}… enregistré`);
    }
    return token;
  } catch (error) {
    console.warn('[push] inscription impossible', error);
    return null;
  }
}
